import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import LicenseDetails from "./LicenseDetails";
import {
  fetchLicenseDetails,
  getLocationChargebeeMap,
  getSingleChargebeeCustomer,
  getAllChargebeeCustomers,
  setLocationChargebeeMap,
  deleteLocationChargebeeMap,
  getLocationPasses,
} from "../../../../services/locationApp/licenseService";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import Cookies from "js-cookie";

interface License {
  licenseCode: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
  subscriptionId?: string;
}

interface ChargebeeCustomer {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
}

interface LicensePass {
  expiresAt?: string;
  licenseCode?: string;
}

interface LicensePassGroup {
  licenseCode?: string;
  passes?: LicensePass[];
}

interface LicenseApiResponse {
  status: number;
  message: string;
  timestamp: string;
  data: License[];
}

type LicensePatch = Partial<License>;

const getActivePassLicenseCodes = (passGroups: LicensePassGroup[]) => {
  const now = Date.now();

  return passGroups.reduce<string[]>((licenseCodes, group) => {
    const hasActivePass = (group.passes || []).some((pass) => {
      const expiresAt = new Date(pass.expiresAt || "").getTime();
      return !Number.isNaN(expiresAt) && expiresAt > now;
    });

    if (group.licenseCode && hasActivePass) {
      licenseCodes.push(group.licenseCode);
    }

    return licenseCodes;
  }, []);
};

const LicenseDetailsContainer: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();

  const [licenses, setLicenses] = useState<License[]>([]);
  const [activePassLicenseCodes, setActivePassLicenseCodes] = useState<string[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [chargebeeCustomerId, setChargebeeCustomerId] = useState<string | null>(
    null
  );
  const [chargebeeCustomer, setChargebeeCustomer] =
    useState<ChargebeeCustomer | null>(null);
  const [chargebeeLoading, setChargebeeLoading] = useState(false);
  const [chargebeeError, setChargebeeError] = useState("");

  const [chargebeeModalOpen, setChargebeeModalOpen] = useState(false);
  const [chargebeeCustomers, setChargebeeCustomers] = useState<
    ChargebeeCustomer[]
  >([]);
  const [chargebeeCustomersLoading, setChargebeeCustomersLoading] =
    useState(false);

  const getToken = () => Cookies.get("idToken") || null;

  const extractChargebeeCustomerId = (response: any): string | null => {
    const payload = response?.data?.data;

    if (!payload) return null;

    if (typeof payload === "string") return payload;

    return (
      payload.chargebeeCustomerId ||
      payload.customerId ||
      payload.chargebee_customer_id ||
      null
    );
  };

  const extractCustomerList = (response: any): ChargebeeCustomer[] => {
    const payload = response?.data?.data;

    const rawList =
      payload?.list ||
      payload?.customers ||
      response?.data?.list ||
      response?.data?.customers ||
      [];

    return rawList
      .map((item: any) => item.customer || item)
      .filter((customer: any) => customer && customer.id);
  };

  const loadChargebeeCustomer = async (selectedLocationId: string, token: string) => {
    setChargebeeLoading(true);
    setChargebeeError("");

    try {
      const mapResponse = await getLocationChargebeeMap(
        selectedLocationId,
        token
      );

      const mappedCustomerId = extractChargebeeCustomerId(mapResponse);

      setChargebeeCustomerId(mappedCustomerId);

      if (!mappedCustomerId) {
        setChargebeeCustomer(null);
        return;
      }

      const customerResponse = await getSingleChargebeeCustomer(
        locationId,
        token
      );

      const customer = customerResponse?.data?.data?.customer || null;
      setChargebeeCustomer(customer);
    } catch (err) {
      console.error("❌ Chargebee customer fetch error:", err);
      setChargebeeError("Unable to load Chargebee customer.");
      setChargebeeCustomer(null);
      setChargebeeCustomerId(null);
    } finally {
      setChargebeeLoading(false);
    }
  };

  const loadLicenses = async () => {
    if (!locationId) return;

    setLoading(true);
    setError("");

    try {
      const token = await getToken();

      if (!token) throw new Error("Missing Firebase token");

      const [response, passesResponse] = await Promise.all([
        fetchLicenseDetails(locationId, token),
        getLocationPasses(locationId, token),
      ]);
      const data: LicenseApiResponse = response.data;

      setLicenses(data.data || []);
      setActivePassLicenseCodes(
        getActivePassLicenseCodes(passesResponse?.data?.data || []),
      );

      await loadChargebeeCustomer(locationId, token);
    } catch (err) {
      console.error("❌ License fetch error:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setLicenses([]);
      setActivePassLicenseCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshLicensesOnly = async () => {
    if (!locationId) return;

    try {
      const token = await getToken();

      if (!token) throw new Error("Missing Firebase token");

      const [response, passesResponse] = await Promise.all([
        fetchLicenseDetails(locationId, token),
        getLocationPasses(locationId, token),
      ]);
      const data: LicenseApiResponse = response.data;

      setLicenses(data.data || []);
      setActivePassLicenseCodes(
        getActivePassLicenseCodes(passesResponse?.data?.data || []),
      );
    } catch (err) {
      console.error("❌ License-only refresh error:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  const handleLicenseUpdate = (licenseCode: string, patch: LicensePatch) => {
    setLicenses((currentLicenses) =>
      currentLicenses.map((license) =>
        license.licenseCode === licenseCode ? { ...license, ...patch } : license,
      ),
    );
  };

  const handleOpenChargebeeModal = async () => {
    const token = await getToken();

    if (!token) {
      setChargebeeError("Missing Firebase token.");
      return;
    }

    setChargebeeModalOpen(true);
    setChargebeeCustomersLoading(true);

    try {
      const response = await getAllChargebeeCustomers(token);
      const customers = extractCustomerList(response);
      setChargebeeCustomers(customers);
    } catch (err) {
      console.error("❌ Chargebee customers fetch error:", err);
      setChargebeeCustomers([]);
      setChargebeeError("Unable to load Chargebee customers.");
    } finally {
      setChargebeeCustomersLoading(false);
    }
  };

  const handleSelectChargebeeCustomer = async (customer: ChargebeeCustomer) => {
    if (!locationId) return;

    const token = await getToken();

    if (!token) {
      setChargebeeError("Missing Firebase token.");
      return;
    }

    try {
      await setLocationChargebeeMap(
        locationId,
        {
          chargebeeCustomerId: customer.id,
        },
        token
      );

      setChargebeeCustomerId(customer.id);
      setChargebeeCustomer(customer);
      setChargebeeModalOpen(false);

      await loadChargebeeCustomer(locationId, token);
    } catch (err) {
      console.error("❌ Set Chargebee customer error:", err);
      setChargebeeError("Unable to connect Chargebee customer.");
    }
  };

  const handleDisconnectChargebeeCustomer = async () => {
    if (!locationId) return;

    const token = await getToken();

    if (!token) {
      setChargebeeError("Missing Firebase token.");
      return;
    }

    try {
      await deleteLocationChargebeeMap(locationId, token);
      setChargebeeCustomerId(null);
      setChargebeeCustomer(null);
      setChargebeeError("");
    } catch (err) {
      console.error("❌ Delete Chargebee customer map error:", err);
      setChargebeeError("Unable to disconnect Chargebee customer.");
    }
  };

  useEffect(() => {
    document.title = "Location - License";
    loadLicenses();
  }, [locationId]);

  return (
    <LicenseDetails
      locationId={locationId}
      licenses={licenses}
      activePassLicenseCodes={activePassLicenseCodes}
      loading={loading}
      error={error}
      onReload={refreshLicensesOnly}
      chargebeeCustomerId={chargebeeCustomerId}
      chargebeeCustomer={chargebeeCustomer}
      chargebeeLoading={chargebeeLoading}
      chargebeeError={chargebeeError}
      onConnectChargebee={handleOpenChargebeeModal}
      onDisconnectChargebee={handleDisconnectChargebeeCustomer}
      chargebeeModalOpen={chargebeeModalOpen}
      onCloseChargebeeModal={() => setChargebeeModalOpen(false)}
      chargebeeCustomers={chargebeeCustomers}
      chargebeeCustomersLoading={chargebeeCustomersLoading}
      onSelectChargebeeCustomer={handleSelectChargebeeCustomer}
      onLicenseUpdate={handleLicenseUpdate}
    />
  );
};

export default LicenseDetailsContainer;
